<template>
    <div class="activity-tab-container">
        <div class="activity-header">
            <p class="activity-title">Recent Activity with {{ company?.name || 'Company' }}</p>
        </div>

        <SharedSpinner v-if="pending" label="Loading activity..." />

        <div v-else-if="conversation.length > 0" class="activity-content">
            <ActivityFeed :activities="conversation" :loading="pending" :exposure-status="exposure?.status" />
        </div>

        <div v-else class="no-activity">
            <p class="text-gray-500 text-center py-8">No Activity Found</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import ActivityFeed from '~/components/shared/ActivityFeed.vue';
import { exposuresService } from '~/services/exposures-service';
import { useExposureSidebarStore } from '~/stores/components/modals/exposure-sidebar';
import { usePersonStore } from '~/stores/person';
import type { ConversationMessage } from '~/types/companies/conversations';

const personStore = usePersonStore();
const exposureStore = useExposureSidebarStore();
const { exposure, company } = storeToRefs(exposureStore);

const conversation = ref<ConversationMessage[]>([]);
const pending = ref(true);

const fetchActivityData = async (): Promise<void> => {
    if (!personStore.person || !company.value?.id) {
        conversation.value = [];
        pending.value = false;
        return;
    }

    try {
        pending.value = true;
        // Use company history instead of exposure history to get all activity between person and company
        const history = await exposuresService.getCompanyHistory(
            personStore.person.id,
            company.value.id
        );
        conversation.value = history || [];
    } catch (error: any) {
        console.error('Failed to fetch activity data:', error);
        conversation.value = [];
    } finally {
        pending.value = false;
    }
};

onMounted(() => {
    fetchActivityData();
});

watch([() => exposureStore.selectedExposure, () => company.value?.id], () => {
    // Refetch when exposure or company changes
    fetchActivityData();
}, { immediate: false });

// Also watch for company to become available (in case it loads after selectedCompanyId is set)
watch(() => company.value, (newCompany) => {
    if (newCompany?.id && personStore.person?.id) {
        fetchActivityData();
    }
}, { immediate: false });
</script>

<style lang="scss" scoped>
.activity-tab-container {
    @apply flex flex-col;
}

.activity-header {
    @apply px-6 py-5;
}

.activity-title {
    @apply text-zinc-950 font-inter text-base font-semibold leading-7;
}

.activity-content {
    @apply pb-4;
}

.no-activity {
    @apply text-center py-8;
}
</style>

